/**
 * Admin Statistics API
 * ადმინისტრატორის სტატისტიკის API
 */

import { get } from '@aiia/portal-core';

export type StatisticsPeriod = 'TODAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface PlatformOverviewResponse {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  totalWidgets: number;
  activeWidgets: number;
  totalApiKeys: number;
  activeApiKeys: number;
  totalDataFiles: number;
  processedDataFiles: number;
  totalStorageUsedBytes: number;
  totalConversations: number;
  conversationsToday: number;
  generatedAt: string;
}

export interface UserStatisticsResponse {
  totalUsers: number;
  usersByStatus: Record<string, number>;
  usersByEntityType: Record<string, number>;
  registrationTrend: TimeSeriesPoint[];
  topActiveUsers: Array<{
    userId: string;
    userName: string;
    widgetCount: number;
    conversationCount: number;
  }>;
}

export interface WidgetStatisticsResponse {
  totalWidgets: number;
  activeWidgets: number;
  deletedWidgets: number;
  widgetsByPosition: Record<string, number>;
  creationTrend: TimeSeriesPoint[];
  topWidgets: Array<{
    widgetId: string;
    widgetName: string;
    conversationCount: number;
    messageCount: number;
  }>;
}

export interface RevenueStatisticsResponse {
  mrr: number;
  revenueThisMonth: number;
  revenueThisYear: number;
  subscriptionsByPlan: Record<string, number>;
  subscriptionsByStatus: Record<string, number>;
  revenueTrend: TimeSeriesPoint[];
  activeSubscriptions: number;
  trialSubscriptions: number;
  canceledThisMonth: number;
}

export interface GeminiUsageStatisticsResponse {
  totalKeys: number;
  activeKeys: number;
  totalDailyUsage: number;
  totalDailyLimit: number;
  utilizationPercentage: number;
  keyUsage: Array<{
    keyId: string;
    keyName: string;
    dailyUsage: number;
    dailyLimit: number;
    assignedUsers: number;
  }>;
}

export interface DataFilesStatisticsResponse {
  totalFiles: number;
  processedFiles: number;
  pendingFiles: number;
  failedFiles: number;
  totalStorageBytes: number;
  filesByType: Record<string, number>;
  filesByStatus: Record<string, number>;
  uploadTrend: TimeSeriesPoint[];
}

export interface ServiceHealth {
  name: string;
  status: 'UP' | 'DOWN' | 'UNKNOWN';
  responseTimeMs: number;
  details: Record<string, unknown>;
}

export interface SystemHealthResponse {
  services: ServiceHealth[];
  checkedAt: string;
}

// Get system health
export const getSystemHealth = () =>
  get<SystemHealthResponse>('/admin/statistics/health');

// Get platform overview
export const getPlatformOverview = () =>
  get<PlatformOverviewResponse>('/admin/statistics/overview');

// Get user statistics
export const getUserStatistics = (period: StatisticsPeriod = 'MONTH') =>
  get<UserStatisticsResponse>(`/admin/statistics/users?period=${period}`);

// Get widget statistics
export const getWidgetStatistics = (period: StatisticsPeriod = 'MONTH') =>
  get<WidgetStatisticsResponse>(`/admin/statistics/widgets?period=${period}`);

// Get revenue statistics
export const getRevenueStatistics = (period: StatisticsPeriod = 'MONTH') =>
  get<RevenueStatisticsResponse>(`/admin/statistics/revenue?period=${period}`);

// Get Gemini usage statistics
export const getGeminiStatistics = () =>
  get<GeminiUsageStatisticsResponse>('/admin/statistics/gemini');

// Get data files statistics
export const getDataFilesStatistics = (period: StatisticsPeriod = 'MONTH') =>
  get<DataFilesStatisticsResponse>(`/admin/statistics/data-files?period=${period}`);
